import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Loader2, Edit3, Trash2 } from "lucide-react";
import { PageHeader, StatusBadge, fmtDateTime, Empty } from "../../components/admin/ui";

const TYPES = ["general", "service", "event", "prayer_call", "fasting", "fundraiser"];
const STATUSES = ["draft", "published", "archived"];
const empty = { title: "", body: "", type: "general", status: "draft", publish_at: "" };

const Announcements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/announcements"); setItems(r.data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = (a) => setEditing(a ? { ...a } : { ...empty });

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.body) { toast.error("Title and body required."); return; }
    const payload = { title: editing.title, body: editing.body, type: editing.type, status: editing.status, publish_at: editing.publish_at || null };
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/announcements/${editing.id}`, payload);
      else await api.post("/admin/announcements", payload);
      toast.success("Saved."); setEditing(null); load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/admin/announcements/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="space-y-8" data-testid="admin-announcements">
      <PageHeader
        overline="The Pulpit"
        title={<><span className="italic gold-text">Announcements</span></>}
        subtitle="Compose ministry-wide messages, prayer calls, fasting calls, and event updates."
      >
        <button onClick={() => open(null)} className="btn-fire" data-testid="add-announcement-btn"><Plus className="h-4 w-4" /> New Announcement</button>
      </PageHeader>

      {loading ? (
        <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <Empty title="No announcements yet" subtitle="Create one to keep covenant partners and members aligned with what the Spirit is saying." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map((a) => (
            <article key={a.id} className="bento-card !p-6" data-testid={`ann-${a.id}`}>
              <span className="glow-blob" />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{a.type}</div>
                  <h3 className="font-serif-display text-xl text-[#F6F0E6] mt-1 truncate">{a.title}</h3>
                  <p className="text-xs text-[#D9D9D9]/70 mt-1">{fmtDateTime(a.created_at)}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
              <p className="mt-3 text-sm text-[#D9D9D9] line-clamp-3 whitespace-pre-wrap">{a.body}</p>
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => open(a)} className="btn-ghost !py-2 !px-3 !text-xs"><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => del(a.id)} className="ml-auto h-9 w-9 inline-flex items-center justify-center rounded-full border border-[#E8452C]/30 text-[#E8452C] hover:bg-[#E8452C]/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#050505]/85 backdrop-blur-md flex items-center justify-center px-6 py-10 overflow-y-auto" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bento-card !p-8 max-w-2xl w-full space-y-5" data-testid="announcement-form">
            <span className="glow-blob" />
            <div className="flex items-center justify-between">
              <div><div className="overline">{editing.id ? "Edit" : "New"}</div><h2 className="font-serif-display text-2xl text-[#F6F0E6] mt-1">Announcement</h2></div>
              <button type="button" onClick={() => setEditing(null)} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div><label className="field-label">Title</label>
              <input className="input-fire" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="ann-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">Type</label>
                <select className="input-fire" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="ann-type">
                  {TYPES.map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Status</label>
                <select className="input-fire" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="ann-status">
                  {STATUSES.map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s}</option>)}
                </select>
              </div>
            </div>
            <div><label className="field-label">Body</label>
              <textarea rows={8} className="input-fire" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="ann-body" /></div>
            <button type="submit" disabled={saving} className="btn-fire" data-testid="ann-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {saving ? "Saving…" : editing.id ? "Save Changes" : "Create Announcement"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Announcements;
